import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './firebase/client';
import { toast } from 'sonner';

export interface UserPreferences {
  homepage: 'dashboard' | 'wells' | 'routes' | 'analytics' | 'tasks';
  theme: 'light' | 'dark' | 'auto';
  navigationMode: 'internal' | 'external';
  defaultMapsApp?: 'google' | 'apple' | 'waze' | 'system';
  notificationSettings?: any; // Full notification preferences
}

/**
 * Find the correct user document reference (supports both old flat and new tenant-scoped collections)
 * Returns { ref, exists } to allow callers to handle document creation if needed
 */
async function findUserDocRef(userId: string, companyId?: string): Promise<{ ref: any; exists: boolean; path: string }> {

  // First try tenant-scoped collection if companyId is provided
  if (companyId && companyId !== 'default') {
    try {
      const tenantUserRef = doc(db, 'companies', companyId, 'users', userId);
      const tenantUserDoc = await getDoc(tenantUserRef);
      if (tenantUserDoc.exists()) {
        return { ref: tenantUserRef, exists: true, path: tenantUserRef.path };
      } else {
      }
    } catch (error: any) {
    }
  }

  // Try old flat collection
  const flatRef = doc(db, 'users', userId);
  try {
    const flatDoc = await getDoc(flatRef);
    if (flatDoc.exists()) {
      return { ref: flatRef, exists: true, path: flatRef.path };
    }
  } catch (error: any) {
  }

  // If neither exists, prefer tenant-scoped path if companyId provided
  if (companyId && companyId !== 'default') {
    const tenantUserRef = doc(db, 'companies', companyId, 'users', userId);
    return { ref: tenantUserRef, exists: false, path: tenantUserRef.path };
  }

  return { ref: flatRef, exists: false, path: flatRef.path };
}

/**
 * Updates user preferences in Firestore
 * UPDATED: Supports both old flat /users and new tenant-scoped /companies/{companyId}/users collections
 * Uses setDoc with merge as fallback if document doesn't exist
 */
export async function updateUserPreferences(
  userId: string,
  preferences: Partial<UserPreferences>,
  companyId?: string
): Promise<boolean> {
  try {

    const { ref: userRef, exists, path } = await findUserDocRef(userId, companyId);

    // Convert partial preferences object to dot notation to avoid overwriting the entire map
    const updates: Record<string, any> = {
      updatedAt: new Date()
    };

    Object.keys(preferences).forEach(key => {
      const value = preferences[key as keyof UserPreferences];
      if (value !== undefined) {
        updates[`preferences.${key}`] = value;
      }
    });


    if (exists) {
      // Document exists, use updateDoc
      await updateDoc(userRef, updates);
    } else {
      // Document doesn't exist, use setDoc with merge
      await setDoc(userRef, { preferences, updatedAt: new Date() }, { merge: true });
    }


    return true;
  } catch (error: any) {
    console.error('❌ Error updating user preferences:', error);
    console.error('   Error code:', error.code);
    console.error('   Error message:', error.message);
    toast.error('Failed to save preferences');
    return false;
  }
}

/**
 * Updates navigation mode preference
 */
export async function updateNavigationMode(
  userId: string,
  navigationMode: 'internal' | 'external',
  companyId?: string
): Promise<boolean> {
  return updateUserPreferences(userId, { navigationMode }, companyId);
}

/**
 * Updates default maps app preference
 */
export async function updateDefaultMapsApp(
  userId: string,
  defaultMapsApp: 'google' | 'apple' | 'waze' | 'system',
  companyId?: string
): Promise<boolean> {
  return updateUserPreferences(userId, { defaultMapsApp }, companyId);
}

/**
 * Gets user preferences with defaults
 */
export function getUserPreferencesWithDefaults(user: any): UserPreferences {
  const defaultPreferences: UserPreferences = {
    homepage: 'dashboard',
    theme: 'auto',
    navigationMode: 'internal', // Default to internal navigation
  };

  return {
    ...defaultPreferences,
    ...user?.preferences
  };
}